/**
 * ParticipantsTab
 * ─────────────────
 * Lists participants for this event.
 * Uses GET /participants?eventId=  (admin route)
 * Supports Add, Edit (drawer), Delete.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Alert, Drawer, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Tooltip, Chip, Skeleton, FormControlLabel, Switch,
  CircularProgress,
} from '@mui/material';
import {
  AddOutlined, EditOutlined, DeleteOutlined, CloseOutlined,
  SaveOutlined, PeopleOutlined, SearchOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../../hooks/useApi';
import { PRIMARY, INPUT_SX, FormField, SWITCH_SX } from '../shared';

// ── Helpers ───────────────────────────────────────────────────────────────────
function initials(p) {
  const n = [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || '?';
  return n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}
function fullName(p) {
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';
}
function avatarColor(email = '') {
  const hue = [...email].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return `hsl(${hue}, 60%, 48%)`;
}

// ── Drawer form ───────────────────────────────────────────────────────────────
const BLANK = {
  firstName: '', lastName: '', email: '',
  password: '', employeeNumber: '',
  isActive: true,
};

function ParticipantDrawer({ open, participant, eventId, onClose, onSaved }) {
  const [form, setForm]     = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    setError('');
    if (participant) {
      setForm({ ...BLANK, ...participant, password: '' });
    } else {
      setForm({ ...BLANK });
    }
  }, [participant, open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.email.trim()) { setError('Email is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, event: eventId };
      // Don't send empty password on edit
      if (participant?._id && !form.password) delete payload.password;

      let result;
      if (participant?._id) {
        const r = await api.put(`/participants/${participant._id}`, payload);
        result = r.data.data;
      } else {
        const r = await api.post('/participants', payload);
        result = r.data.data;
      }
      onSaved(result);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {participant ? 'Edit Participant' : 'Add Participant'}
        </Typography>
        <IconButton size="small" onClick={onClose}><CloseOutlined sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <FormField label="First Name">
              <TextField value={form.firstName} onChange={e => set('firstName', e.target.value)}
                fullWidth size="small" sx={INPUT_SX} />
            </FormField>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormField label="Last Name">
              <TextField value={form.lastName} onChange={e => set('lastName', e.target.value)}
                fullWidth size="small" sx={INPUT_SX} />
            </FormField>
          </Box>
        </Box>

        <FormField label="Email" required>
          <TextField value={form.email} onChange={e => set('email', e.target.value)}
            fullWidth size="small" type="email" sx={INPUT_SX} />
        </FormField>

        <FormField label={participant ? 'Password (leave blank to keep current)' : 'Password'}>
          <TextField value={form.password} onChange={e => set('password', e.target.value)}
            fullWidth size="small" type="password" sx={INPUT_SX} />
        </FormField>

        <FormField label="Employee Number">
          <TextField value={form.employeeNumber || ''} onChange={e => set('employeeNumber', e.target.value)}
            fullWidth size="small" sx={INPUT_SX} />
        </FormField>

        <FormControlLabel
          control={<Switch checked={!!form.isActive} onChange={e => set('isActive', e.target.checked)} size="small" sx={SWITCH_SX} />}
          label={
            <Box>
              <Box component="span" sx={{ fontWeight: 600, fontSize: '0.875rem', display: 'block' }}>Active</Box>
              <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Participant can log in</Box>
            </Box>
          }
        />
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8edf2', flexShrink: 0, background: '#fafbfc' }}>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlined />}
            disabled={saving}
            sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ParticipantsTab({ event, isActive = false }) {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [editing, setEditing]           = useState(null);

  const load = useCallback(async () => {
    if (!event?._id) return;
    setLoading(true); setError('');
    try {
      const r = await api.get(`/participants?eventId=${event._id}&limit=200`);
      setParticipants(r.data.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load participants');
    } finally { setLoading(false); }
  }, [event?._id]);

  useEffect(() => { load(); }, [load]);

  // Refetch when the user navigates back to this tab (skip initial mount)
  const mountedRef = React.useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (isActive) load();
  }, [isActive]); // eslint-disable-line

  const handleSaved = (p) => {
    setParticipants(prev => {
      const idx = prev.findIndex(x => x._id === p._id);
      return idx >= 0 ? prev.map((x, i) => i === idx ? p : x) : [p, ...prev];
    });
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/participants/${id}`);
      setParticipants(prev => prev.filter(p => p._id !== id));
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    }
  };

  const filtered = participants.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.firstName || '').toLowerCase().includes(q) ||
      (p.lastName  || '').toLowerCase().includes(q) ||
      (p.email     || '').toLowerCase().includes(q) ||
      (p.employeeNumber || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {error && <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1a1a2e' }}>
          Participants
          {!loading && (
            <Chip label={participants.length} size="small"
              sx={{ ml: 1, height: 20, fontSize: '0.72rem', background: alpha(PRIMARY, 0.08), color: PRIMARY }} />
          )}
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddOutlined />}
          onClick={() => { setEditing(null); setDrawerOpen(true); }}
          sx={{ borderRadius: 2, background: PRIMARY, '&:hover': { background: '#0d5fa8' }, textTransform: 'none' }}>
          Add Participant
        </Button>
      </Box>

      {/* Search */}
      <TextField
        value={search} onChange={e => setSearch(e.target.value)}
        fullWidth size="small" placeholder="Search by name, email or employee number…"
        sx={INPUT_SX}
        InputProps={{ startAdornment: <SearchOutlined sx={{ fontSize: 18, color: 'text.secondary', mr: 0.75 }} /> }}
      />

      {/* Table */}
      <Box sx={{ border: '1px solid #e8edf2', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(5)].map((_, i) => <Skeleton key={i} height={48} sx={{ mb: 0.5, borderRadius: 2 }} />)}
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 9 }}>
            <PeopleOutlined sx={{ fontSize: 44, color: alpha(PRIMARY, 0.15), mb: 1.5 }} />
            <Typography sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              {search ? 'No participants match your search' : 'No participants yet'}
            </Typography>
            {!search && (
              <Button variant="contained" startIcon={<AddOutlined />} sx={{ mt: 2, borderRadius: 2, background: PRIMARY }}
                onClick={() => { setEditing(null); setDrawerOpen(true); }}>
                Add First Participant
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f8f9fb' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Participant</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Employee #</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#5a6474', py: 1.5 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(p => (
                  <TableRow key={p._id} hover sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                    onClick={() => { setEditing(p); setDrawerOpen(true); }}>
                    <TableCell sx={{ py: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar src={p.avatar || undefined}
                          sx={{ width: 34, height: 34, fontSize: '0.78rem', fontWeight: 700, bgcolor: avatarColor(p.email) }}>
                          {!p.avatar && initials(p)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{fullName(p)}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{p.employeeNumber || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.isActive ? 'Active' : 'Inactive'} size="small"
                        sx={{
                          height: 20, fontSize: '0.7rem',
                          background: p.isActive ? alpha('#00a882', 0.08) : alpha('#888', 0.08),
                          color: p.isActive ? '#00a882' : '#888',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); setEditing(p); setDrawerOpen(true); }}
                            sx={{ color: '#c0c8d4', '&:hover': { color: PRIMARY } }}>
                            <EditOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={e => { e.stopPropagation(); handleDelete(p._id); }}
                            sx={{ color: '#c0c8d4', '&:hover': { color: '#d32f2f' } }}>
                            <DeleteOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ParticipantDrawer
        open={drawerOpen}
        participant={editing}
        eventId={event?._id}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
      />
    </Box>
  );
}